import { Link, router } from "@inertiajs/react"
import { useEffect, useMemo, useRef, useState } from "react"
import {Clock, Edit, Eye,  Plus, Shield, UserCheck, Users} from "lucide-react"
import { Badge } from "@/components/ui/badge.tsx"
import { Button } from "@/components/ui/button.tsx"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card.tsx"
import { Input } from "@/components/ui/input.tsx"
import Pagination from "@/components/pagination.tsx"
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "@/components/ui/table.tsx"
import type { PaginationType, UserType, UsersFiltersType } from "./types.ts"


interface IndexProps {
    users: UserType[]
    filters: UsersFiltersType
    pagination: PaginationType
    counts: {
        total: number
        active: number
        admins: number
        managers: number
    }
}

const Index = ({ users, filters, pagination, counts }: IndexProps) => {
    const hasInitialized = useRef(false)
    const [name, setName] = useState(filters.name)
    const [role, setRole] = useState(filters.role)
    const [status, setStatus] = useState(filters.status)

    useEffect(() => {
        setName(filters.name)
        setRole(filters.role)
        setStatus(filters.status)
    }, [filters.name, filters.role, filters.status])

    const query = useMemo(() => ({
        name,
        role,
        status
    }), [name, role, status])

    useEffect(() => {
        if (!hasInitialized.current) {
            hasInitialized.current = true
            return
        }

        const timeoutId = setTimeout(() => {
            router.get("/admin/users", query, {
                preserveState: true,
                preserveScroll: true,
                replace: true
            })
        }, 350)

        return () => clearTimeout(timeoutId)
    }, [query])

    return (
        <div className="space-y-6 p-8">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-foreground">Les utilisateurs</h1>
                    <p className="text-muted-foreground">Gestion des utilisateurs et permissions</p>
                </div>
                <Link href="/admin/users/new">
                    <Button>
                        <Plus className="mr-2 h-4 w-4" />
                        Ajouter un nouvel utilisateur
                    </Button>
                </Link>
            </div>

            <div className="grid gap-4 md:grid-cols-4">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total utilisateurs</CardTitle>
                        <Users className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{counts.total}</div>
                        <p className="text-xs text-muted-foreground">Filtrés par la vue actuelle</p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Utilisateurs actifs</CardTitle>
                        <UserCheck className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{counts.active}</div>
                        <p className="text-xs text-muted-foreground">Comptes actifs</p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Administrateurs</CardTitle>
                        <Shield className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{counts.admins}</div>
                        <p className="text-xs text-muted-foreground">Accès admin</p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Managers</CardTitle>
                        <Shield className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{counts.managers}</div>
                        <p className="text-xs text-muted-foreground">Accès manager</p>
                    </CardContent>
                </Card>
            </div>

            <Card className="p-4">
                <div className="grid gap-3 md:grid-cols-4">
                    <Input
                        type="text"
                        name="name"
                        placeholder="Filtrer par prénom ou nom"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        className="md:col-span-2"
                    />
                    <select
                        name="role"
                        value={role}
                        onChange={(event) => setRole(event.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
                    >
                        <option value="">Tous les rôles</option>
                        <option value="agent">Agent</option>
                        <option value="admin">Administrateur</option>
                        <option value="super_admin">Super administrateur</option>
                        <option value="manager">Gestionnaire</option>
                    </select>
                    <select
                        name="status"
                        value={status}
                        onChange={(event) => setStatus(event.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
                    >
                        <option value="">Tous les statuts</option>
                        <option value="active">Actif</option>
                        <option value="inactive">Inactif</option>
                    </select>
                    <div className="md:col-span-4 flex items-center gap-2">
                        <Button
                            variant="outline"
                            type="button"
                            onClick={() => {
                                setName("")
                                setRole("")
                                setStatus("")
                            }}
                        >
                            Réinitialiser
                        </Button>
                    </div>
                </div>
            </Card>

            <Card className="p-4">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Code</TableHead>
                            <TableHead>Prénom</TableHead>
                            <TableHead>Nom</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Rôle</TableHead>
                            <TableHead>Statut</TableHead>
                            <TableHead>Dernière connexion</TableHead>
                            <TableHead>Communauté</TableHead>
                            <TableHead>Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {users.length === 0 ? (
                            <TableRow>
                                <TableCell className="text-center text-muted-foreground" colSpan={9}>
                                    Aucun utilisateur trouvé.
                                </TableCell>
                            </TableRow>
                        ) : (
                            users.map((user) => (
                                <TableRow key={user.id}>
                                    <TableCell>{user.code}</TableCell>
                                    <TableCell>{user.first_name}</TableCell>
                                    <TableCell>{user.last_name}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>{user.role}</TableCell>
                                    <TableCell>
                                        <Badge variant={user.status === "active" ? "default" : "destructive"}>
                                            {user.status}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        <div className="flex items-center gap-2">
                                            <Clock className="h-3 w-3" />
                                            {user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleString() : "-"}
                                        </div>
                                    </TableCell>

                                    <TableCell>{user.first_community_name || "-"}</TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-2">
                                            <Link href={`/admin/users/${user.id}`}>
                                                <Button variant="ghost" size="icon">
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                            </Link>
                                            <Link href={`/admin/users/${user.id}/edit`}>
                                                <Button variant="ghost" size="icon">
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                            </Link>
                                        </div>
                                    </TableCell>

                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Card>

            <Pagination
                currentPage={pagination.current_page}
                totalPages={pagination.total_pages}
                basePath="/admin/users"
                query={query}
            />

        </div>
    )
}

export default Index
